/**
 * Repository Implementation: Reward Catalog
 * Integrates with RewardCatalogApiService for API calls.
 */

import { inject, injectable } from "inversify";
import type {
  VoucherListItem,
  VoucherDetail,
  VoucherListQuery,
  VoucherListResult,
  CreateVoucherPayload,
  CreateVoucherResult,
  UpdateVoucherPayload,
  UpdateVoucherResult,
  CreateEWalletPayload,
  CreateEWalletResult,
  CreatePointConversionPayload,
  CreatePointConversionResult,
  EWalletProvider,
  PointConversionPartner,
  MerchantDropdownItem,
  VoucherCategoryDropdownItem,
  VoucherCodesValidationResult,
  VoucherkuCategory,
  VoucherkuProduct,
  ImportVoucherkuPayload,
  ImportVoucherkuResult,
} from "../../domain/model/RewardCatalogModel";
import type { IRewardCatalogRepository } from "../../domain/repository/IRewardCatalogRepository";
import { RewardCatalogApiService } from "../remote/service/RewardCatalogApiService";

@injectable()
export class RewardCatalogRepositoryImpl implements IRewardCatalogRepository {
  @inject(RewardCatalogApiService)
  private readonly apiService!: RewardCatalogApiService;

  async fetchVoucherList(query?: VoucherListQuery): Promise<VoucherListResult> {
    const response = await this.apiService.fetchVoucherList(query);

    return {
      items: response.data.map((dto) => this.mapDtoToListItem(dto)),
      paging: {
        currentPage: response.paging.currentPage,
        pageSize: response.paging.pageSize,
        totalPages: response.paging.totalPages,
        totalRecords: response.paging.totalRecords,
      },
    };
  }

  async fetchVoucherDetail(voucherId: string): Promise<VoucherDetail> {
    const response = await this.apiService.fetchVoucherDetail(voucherId);
    return this.mapDtoToDetail(response.data);
  }

  async createVoucher(
    payload: CreateVoucherPayload
  ): Promise<CreateVoucherResult> {
    const response = await this.apiService.createVoucher({
      merchantId: payload.merchantId,
      categoryId: payload.categoryId,
      pointsRequired: payload.pointsRequired,
      title: payload.title,
      description: payload.description,
      totalStock: payload.totalStock,
      validFrom: payload.validFrom ?? "",
      validUntil: payload.validUntil ?? "",
      imageUrls: payload.imageUrls,
      termsAndConditions: payload.termsAndConditions,
      howToUse: payload.howToUse,
      voucherCodes: payload.voucherCodes,
      isPopular: payload.isPopular,
      badgeText: payload.badgeText,
      badgeColor: payload.badgeColor,
      status: payload.status,
    });

    return {
      voucherId: response.data.voucherId,
      title: response.data.title,
      status: response.data.status,
      createdAt: response.data.createdAt,
    };
  }

  async updateVoucher(
    voucherId: string,
    payload: UpdateVoucherPayload
  ): Promise<UpdateVoucherResult> {
    const response = await this.apiService.updateVoucher(voucherId, {
      title: payload.title,
      description: payload.description,
      categoryId: payload.categoryId,
      pointsRequired: payload.pointsRequired,
      totalStock: payload.totalStock,
      imageUrls: payload.imageUrls,
      termsAndConditions: payload.termsAndConditions,
      howToUse: payload.howToUse,
      validFrom: payload.validFrom,
      validUntil: payload.validUntil,
      isPopular: payload.isPopular,
      badgeText: payload.badgeText,
      badgeColor: payload.badgeColor,
      status: payload.status,
    });

    return {
      voucherId: response.data.voucherId,
      title: response.data.title,
      status: response.data.status,
      updatedAt: response.data.updatedAt,
    };
  }

  async deleteVoucher(voucherId: string): Promise<void> {
    await this.apiService.deleteVoucher(voucherId);
  }

  async createEWalletConversion(
    payload: CreateEWalletPayload
  ): Promise<CreateEWalletResult> {
    const response = await this.apiService.createEWalletConversion({
      providerId: payload.providerId,
      pointsRequired: payload.pointsRequired,
      rupiahValue: payload.rupiahValue,
      dailyLimitPerUser: payload.dailyLimitPerUser,
      isActive: payload.isActive,
      validFrom: payload.validFrom,
      validUntil: payload.validUntil,
    });

    return {
      conversionId: response.data.conversionId,
      providerId: response.data.providerId,
      pointsRequired: response.data.pointsRequired,
      rupiahValue: response.data.rupiahValue,
    };
  }

  async createPointConversionRate(
    payload: CreatePointConversionPayload
  ): Promise<CreatePointConversionResult> {
    const response = await this.apiService.createPointConversionRate({
      fromPartnerId: payload.fromPartnerId,
      toPartnerId: payload.toPartnerId,
      fromPoints: payload.fromPoints,
      toPoints: payload.toPoints,
      isActive: payload.isActive,
      validUntil: payload.validUntil,
    });

    return {
      rateId: response.data.rateId,
      rateText: response.data.rateText,
    };
  }

  async fetchEWalletProviders(): Promise<readonly EWalletProvider[]> {
    const response = await this.apiService.fetchEWalletProviders();
    return (Array.isArray(response.data) ? response.data : []).map((dto) =>
      this.mapDtoToEWalletProvider(dto)
    );
  }

  async fetchPointConversionPartners(): Promise<readonly PointConversionPartner[]> {
    const response = await this.apiService.fetchPointConversionPartners();
    return (Array.isArray(response.data) ? response.data : []).map((dto) =>
      this.mapDtoToPointConversionPartner(dto)
    );
  }

  async fetchMerchantsDropdown(): Promise<readonly MerchantDropdownItem[]> {
    const response = await this.apiService.fetchMerchantsDropdown();
    return (Array.isArray(response.data) ? response.data : []).map((dto) => ({
      merchantId: dto?.merchantId ?? "",
      merchantName: dto?.merchantName ?? "",
    }));
  }

  async fetchVoucherCategoriesDropdown(): Promise<readonly VoucherCategoryDropdownItem[]> {
    const response = await this.apiService.fetchVoucherCategoriesDropdown();
    return (Array.isArray(response.data) ? response.data : []).map((dto) => ({
      categoryId: dto?.categoryId ?? "",
      name: dto?.name ?? "",
    }));
  }

  async validateVoucherCodesFile(file: File): Promise<VoucherCodesValidationResult> {
    const response = await this.apiService.validateVoucherCodesFile(file);
    const data = response.data;
    return {
      totalCodes: data?.totalCodes ?? 0,
      validCodes: data?.validCodes ?? 0,
      invalidCodes: data?.invalidCodes ?? 0,
      isValid: data?.isValid ?? false,
      errors: Array.isArray(data?.errors) ? data.errors : [],
      warnings: Array.isArray(data?.warnings) ? data.warnings : [],
      duplicateCodes: Array.isArray(data?.duplicateCodes) ? data.duplicateCodes : [],
      voucherCodes: Array.isArray(data?.voucherCodes) ? data.voucherCodes : [],
      fileName: data?.fileName ?? file.name,
      fileSize: data?.fileSize ?? file.size,
    };
  }

  async downloadVoucherTemplate(): Promise<Blob> {
    return this.apiService.downloadVoucherTemplate();
  }

  private mapDtoToListItem(dto: VoucherListItem): VoucherListItem {
    return {
      voucherId: dto?.voucherId ?? "",
      title: dto?.title ?? "",
      brandName: dto?.brandName ?? "",
      categoryId: dto?.categoryId ?? "",
      categoryName: dto?.categoryName ?? "",
      pointsRequired: dto?.pointsRequired ?? 0,
      remainingStock: dto?.remainingStock ?? 0,
      totalStock: dto?.totalStock ?? 0,
      totalRedeemed: dto?.totalRedeemed ?? 0,
      status: dto?.status ?? "",
      isPopular: dto?.isPopular ?? false,
      badgeText: dto?.badgeText ?? "",
      badgeColor: dto?.badgeColor ?? "",
      validFrom: dto?.validFrom ?? "",
      validUntil: dto?.validUntil ?? "",
      createdAt: dto?.createdAt ?? "",
      updatedAt: dto?.updatedAt ?? "",
      createdBy: dto?.createdBy ?? "",
    };
  }

  private mapDtoToDetail(dto: VoucherDetail): VoucherDetail {
    return {
      ...this.mapDtoToListItem(dto),
      description: dto?.description ?? "",
      brandId: dto?.brandId ?? "",
      merchantId: dto?.merchantId ?? "",
      merchantName: dto?.merchantName ?? "",
      imageUrls: Array.isArray(dto?.imageUrls) ? [...dto.imageUrls] : [],
      termsAndConditions: Array.isArray(dto?.termsAndConditions) ? [...dto.termsAndConditions] : [],
      howToUse: Array.isArray(dto?.howToUse) ? [...dto.howToUse] : [],
      applicableStoreIds: Array.isArray(dto?.applicableStoreIds) ? [...dto.applicableStoreIds] : [],
      updatedBy: dto?.updatedBy ?? "",
      provider: dto?.provider ?? "",
    };
  }

  private mapDtoToEWalletProvider(dto: EWalletProvider): EWalletProvider {
    return {
      providerId: dto?.providerId ?? "",
      providerName: dto?.providerName ?? "",
    };
  }

  private mapDtoToPointConversionPartner(dto: PointConversionPartner): PointConversionPartner {
    return {
      partnerId: dto?.partnerId ?? "",
      partnerName: dto?.partnerName ?? "",
    };
  }

  async fetchVoucherkuCategories(): Promise<readonly VoucherkuCategory[]> {
    const response = await this.apiService.fetchVoucherkuCategories();
    return response.data.map((dto) => ({
      categoryId: dto.category_id,
      name: dto.name,
    }));
  }

  async fetchVoucherkuProducts(categoryId?: string): Promise<readonly VoucherkuProduct[]> {
    const response = await this.apiService.fetchVoucherkuProducts(categoryId);
    return response.data.products.map((dto) => ({
      productCode: dto.product_code,
      productName: dto.product_name,
      categoryId: dto.category_id,
      categoryName: dto.category_name,
      price: dto.price,
      description: dto.description,
      stock: dto.stock,
    }));
  }

  async importVoucherku(
    payload: ImportVoucherkuPayload
  ): Promise<ImportVoucherkuResult> {
    const response = await this.apiService.importVoucherku({
      categoryId: payload.categoryId,
      merchantId: payload.merchantId,
      pointsRequired: payload.pointsRequired,
      productCode: payload.productCode,
      validFrom: payload.validFrom,
      validUntil: payload.validUntil,
      title: payload.title,
      description: payload.description,
      isPopular: payload.isPopular,
      badgeText: payload.badgeText,
      badgeColor: payload.badgeColor,
      status: payload.status,
      providerPriority: payload.providerPriority,
      pointsMultiplier: payload.pointsMultiplier,
      imageUrl: payload.imageUrl,
      termsAndConditions: payload.termsAndConditions,
    });

    return {
      voucherId: response.data.voucher_id,
      productCode: response.data.product_code,
      title: response.data.title,
      status: response.data.status,
      createdAt: response.data.created_at,
    };
  }
}
